import { AttackPattern } from '../AttackPattern.js';

/**
 * Faltering Lantern (The Gloam, Act II) — the signature escalation of the
 * darkness fight. The Gloam's whole identity is Gloaming: you only see within
 * a small lit disc around yourself (the disc is a boss-def render property
 * read by Game._drawGloaming, scaled per phase). This move briefly SNUFFS that
 * disc almost to nothing, then fires one slow ring the instant the lights go
 * out — bullets you cannot see coming until they drift into your shrinking
 * bubble. The tell is the dimming itself: over the telegraph the disc visibly
 * collapses (this pattern ramps `boss._gloamRadiusMult` down from 1), so a
 * player who reads it keeps moving and holds their nerve through the blackout.
 *
 * Mechanically it only drives one number — `boss._gloamRadiusMult`, a
 * transient multiplier on the Gloam's vision radius that Game._drawGloaming
 * reads (`?? 1` when unset, so no Boss field declaration is needed and every
 * other boss is unaffected). Restored to 1 in onEnd. The ring is a plain
 * pooled radial so it grazes/collides through the normal SpawningSystem path
 * like any other bullet — the darkness is the only new thing here.
 */
export class FalteringLantern extends AttackPattern {
  constructor({ minPhase = 2, config }) {
    super({ name: 'Faltering Lantern', telegraphDuration: 1.0, activeDuration: 1.2, minPhase });
    this.config = config;
  }

  onBegin(boss, _player) {
    boss.eventBus?.emit('gloam:falter', { x: boss.x, y: boss.y });
  }

  onTelegraphTick(_dt, boss, _player, _pool) {
    // Collapse the lit disc smoothly across the telegraph: mult 1 -> snuffMult.
    const progress = 1 - Math.max(0, this._timer) / this.telegraphDuration;
    boss._gloamRadiusMult = 1 - (1 - this.config.snuffMult) * progress;
  }

  onActiveStart(boss, _player, pool) {
    boss._gloamRadiusMult = this.config.snuffMult;
    // The lights go out and the ring goes off together — slow enough that a
    // moving player can still read bullets as they enter the sliver of vision.
    const count = this.config.ringCount;
    const base = Math.random() * Math.PI * 2;
    for (let i = 0; i < count; i++) {
      const angle = base + (i / count) * Math.PI * 2;
      pool.fire({
        x: boss.x,
        y: boss.y,
        vx: Math.cos(angle) * this.config.ringSpeed,
        vy: Math.sin(angle) * this.config.ringSpeed,
        radius: 2.6,
        damage: this.config.ringDamage,
        color: '#6ea8ff',
        lifetime: 5,
      });
    }
    boss.eventBus?.emit('gloam:snuff', { x: boss.x, y: boss.y });
  }

  onEnd(boss, _player, _pool) {
    boss._gloamRadiusMult = 1;
    boss.eventBus?.emit('gloam:relight');
  }
}
